import {
    Box,
    Divider,
    Drawer,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import type { FunctionComponent } from 'react';
import { SidebarItem, SidebarGroup } from './SidebarItem';
import {
    isOfTypeNavEntryGroup,
    type SidebarEntry,
    type NavEntryItem,
} from './navTypes';
import { Scrollbar } from './Scrollbar';
import { useSidebar } from './SidebarContext';
import { SIDE_NAV_WIDTH } from './constants';

export type SidebarLogo = {
    src: string;
    alt: string;
};

export type SidebarProps = {
    entries: SidebarEntry[];
    logo: SidebarLogo;
    appName: string;
};

export const Sidebar: FunctionComponent<SidebarProps> = ({
    entries,
    logo,
    appName,
}) => {
    const { isMobileOpen, closeMobileSidebar } = useSidebar();
    const theme = useTheme();

    const content = (
        <Scrollbar
            sx={{
                height: '100%',
                '& .simplebar-content': { height: '100%' },
                '& .simplebar-scrollbar:before': { background: 'neutral.400' },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                }}
            >
                <Box sx={{ p: 3 }}>
                    <Box
                        sx={{
                            alignItems: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.04)',
                            borderRadius: 1,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'flex-start',
                            p: '12px',
                        }}
                    >
                        <img
                            src={logo.src}
                            alt={logo.alt}
                            style={{ width: 40, height: 40 }}
                        />
                        <Typography
                            sx={{ ml: 2 }}
                            color='inherit'
                            variant='subtitle1'
                        >
                            {appName}
                        </Typography>
                    </Box>
                </Box>
                <Divider sx={{ borderColor: 'neutral.700' }} />
                <Box component='nav' sx={{ flexGrow: 1, px: 2, py: 3 }}>
                    <Stack
                        component='ul'
                        spacing={0.5}
                        sx={{ listStyle: 'none', p: 0, m: 0 }}
                    >
                        {entries.map((item) => {
                            if (isOfTypeNavEntryGroup(item)) {
                                return (
                                    <SidebarGroup
                                        key={item.title}
                                        icon={item.icon}
                                        title={item.title}
                                        entries={item.entries}
                                    />
                                );
                            }
                            const entry = item as NavEntryItem;
                            return (
                                <SidebarItem
                                    key={entry.title}
                                    disabled={entry.disabled}
                                    external={entry.external}
                                    icon={entry.icon}
                                    path={entry.path}
                                    title={entry.title}
                                    permissionsRequired={
                                        entry.permissionsRequired
                                    }
                                    exact={entry.exact}
                                />
                            );
                        })}
                    </Stack>
                </Box>
            </Box>
        </Scrollbar>
    );

    const drawerPaperSx = {
        backgroundColor: 'neutral.800',
        color: 'common.white',
        width: SIDE_NAV_WIDTH,
    };

    return (
        <>
            <Drawer
                anchor='left'
                onClose={closeMobileSidebar}
                open={isMobileOpen}
                slotProps={{ paper: { sx: drawerPaperSx } }}
                sx={{
                    display: { xs: 'block', lg: 'none' },
                    zIndex: theme.zIndex.appBar,
                }}
                variant='temporary'
            >
                {content}
            </Drawer>
            <Drawer
                anchor='left'
                open
                slotProps={{ paper: { sx: drawerPaperSx } }}
                sx={{ display: { xs: 'none', lg: 'block' } }}
                variant='permanent'
            >
                {content}
            </Drawer>
        </>
    );
};
